import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  AlertTriangle,
  Activity,
  Plus,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Upload,
  Sparkles,
  Check,
  ArrowRight,
  FileText,
  Bot,
  X,
  Loader,
  Trash2,
  Search,
  FolderInput,
  GitMerge
} from 'lucide-react';
import toast from 'react-hot-toast';
import AISOXProcessDesigner from './AISOXProcessDesigner';
import { ProcessGenerateFlowchartModal } from './processes/ProcessGenerateFlowchartModal';
import axiosInstance from '../api/axios';
import useAuthUser from '../hooks/useAuthUser';
import { useSoxDefaultsMappings } from '../hooks/useSoxDefaultsMappings';
import { useProcessById } from '../queries/processes/useProcessById';
import { getAvailableSoxCyclesFromApiData } from '../utils/soxCycleDefaults';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';
import {
  buildProcessReturnState,
  currentPathWithSearch,
  mergeProcessNavigationState,
  soxProcessCreatePath,
  soxProcessesListPath,
  soxProcessFlowchartPath,
  soxCycleEndToEndFlowchartPath,
} from '../routing/processNavigation.ts';
import { Select as StatusSelect } from './common';
import {
  SOX_DASHBOARD_STATUS_OPTIONS,
  normalizeSoxDashboardProcessStatus,
  getSoxDashboardStatusPillTone,
  getCycleRiskLabel,
  getCycleRiskLabelClasses,
  parseMaterialityRiskScoreForPayload,
  MaterialityRiskScoreSlider
} from './sox-dashboard';
import { useSoxDashboardQueries, useSoxDashboardMutations } from '../hooks/sox';
import { patchSoxCycleArchive } from '../request/sox/soxDashboard.api';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { SOX_CYCLES_QUERY_KEY, processesQueryKey } from '../queries/sox';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import { sortProcessesForEndToEnd } from './merge-flowchart/cycleEndToEndProcesses';

function ActiveSwitch({ active, disabled, onToggle, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(!active);
      }}
      className={`relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
        active ? 'bg-emerald-500' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-3 w-3 translate-x-0.5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
          active ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

const flowchartActionButtonClass =
  'inline-flex min-w-[148px] items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-800 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

/** True if API marks entity as archived / soft-deleted (supports common field names). */
function entityIsArchived(entity) {
  if (!entity || typeof entity !== 'object') return false;
  if (
    entity.is_archived === true ||
    entity.is_archived === 1 ||
    entity.archived === true ||
    entity.archived === 1 ||
    entity.isArchived === true ||
    entity.deleted === true ||
    entity.is_deleted === true
  ) {
    return true;
  }
  if (entity.deleted_at != null && entity.deleted_at !== '') return true;
  const st = entity.status ?? entity.lifecycle_status ?? entity.record_status;
  if (typeof st === 'string') {
    const s = st.trim().toLowerCase();
    if (s === 'archived' || s === 'deleted') return true;
  }
  return false;
}

/** True when entity is active; false when inactive. Defaults to active if unset. */
function entityIsActive(entity) {
  if (!entity || typeof entity !== 'object') return true;

  if (typeof entity.is_active === 'boolean') return entity.is_active;
  if (typeof entity.active === 'boolean') return entity.active;
  if (entity.is_active === 0 || entity.active === 0) return false;
  if (entity.is_active === 1 || entity.active === 1) return true;

  if (entity.is_inactive === true || entity.inactive === true) return false;

  const activation = entity.activation_status ?? entity.lifecycle_status;
  if (typeof activation === 'string') {
    const a = activation.trim().toLowerCase();
    if (a === 'inactive' || a === 'disabled') return false;
    if (a === 'active' || a === 'enabled') return true;
  }

  return true;
}

function SOXDashboard({ user }) {
  useAuthUser(user);
  const { selectedAuditYearId } = useAuditYear();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSetupFlow, setShowSetupFlow] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [flowchartMethod, setFlowchartMethod] = useState('ai');
  const [showAIDesigner, setShowAIDesigner] = useState(false);
  const [expandedCycles, setExpandedCycles] = useState([]); // Track which cycles are expanded
  const [selectedCycleForSetup, setSelectedCycleForSetup] = useState(null); // Track cycle being setup
  const [showAddCycleModal, setShowAddCycleModal] = useState(false); // Add cycle modal state
  const [cycleSelection, setCycleSelection] = useState(''); // Selected cycle type (OTC, RTC, PTC, HTC, or 'custom')
  const [customCycleId, setCustomCycleId] = useState(''); // Custom cycle ID
  const [customCycleName, setCustomCycleName] = useState(''); // Custom cycle name
  const [isSavingCycle, setIsSavingCycle] = useState(false); // Loading state
  const [cycleError, setCycleError] = useState(''); // Error message
  /** Module 1 scale (1.0–10.0), sent with create cycle API */
  const [newCycleRiskScore, setNewCycleRiskScore] = useState(5);
  /** Edit cycle risk score from dashboard pill: { cycleListKey, cycleUuid, cycleName } */
  const [editCycleRiskTarget, setEditCycleRiskTarget] = useState(null);
  const [editCycleRiskScore, setEditCycleRiskScore] = useState(5);
  const [editCycleRiskError, setEditCycleRiskError] = useState('');
  /** { process, cycleListKey, processListKey } while generate-flowchart modal is open */
  const [generateFlowchartTarget, setGenerateFlowchartTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Search query for filtering processes
  /** 'all' or a canonical workflow status (matches SOX_DASHBOARD_STATUS_OPTIONS value). */
  const [statusFilter, setStatusFilter] = useState('all');
  /** Confirm dustbin: soft-delete (archive) or restore (unarchive); UI reads as delete/restore only */
  const [cycleArchiveConfirm, setCycleArchiveConfirm] = useState(null);
  const [selectedCycles, setSelectedCycles] = useState([]);
  const [deletingSelectedCycles, setDeletingSelectedCycles] = useState(false);
  /** Confirm dustbin: archive / unarchive process */
  const [processArchiveConfirm, setProcessArchiveConfirm] = useState(null);

  const { soxCyclesFromAPI, loadingCycles, processQueryResults, processesFromAPI } =
    useSoxDashboardQueries(selectedAuditYearId);
  const {
    data: soxDefaultsData,
    isLoading: loadingSoxCycleDefaults,
    isError: soxDefaultsFetchError,
    error: soxDefaultsFetchErrorDetail
  } = useSoxDefaultsMappings();

  /** Standard cycles only from defaults API — no static `soxCycleReference` fallback. */
  const standardCyclesForModal = useMemo(
    () =>
      getAvailableSoxCyclesFromApiData(soxDefaultsData, { strict: true }).map((c) => ({
        id: c.code,
        name: c.name
      })),
    [soxDefaultsData]
  );

  useEffect(() => {
    if (!showAddCycleModal || !cycleSelection || cycleSelection === 'custom') return;
    if (standardCyclesForModal.some((c) => c.id === cycleSelection)) return;
    setCycleSelection('');
  }, [showAddCycleModal, cycleSelection, standardCyclesForModal]);

  const {
    queryClient,
    cyclePatches,
    processPatches,
    patchCycleWorkflowStatusMutation,
    patchCycleRiskScoreMutation,
    patchCycleActiveStatusMutation,
    archiveCycleMutation,
    unarchiveCycleMutation,
    patchProcessWorkflowStatusMutation,
    patchProcessActiveStatusMutation,
    deleteProcessMutation,
    reorderCycleProcessesMutation
  } = useSoxDashboardMutations();

  /** Merge in-flight mutation fields (cleared on error or after refetch on success). */
  const soxCyclesForDisplay = useMemo(
    () =>
      soxCyclesFromAPI.map((c) => {
        const cycleListKey = c.cycle_id ?? c.id;
        const p = cyclePatches[cycleListKey];
        return p ? { ...c, ...p } : c;
      }),
    [soxCyclesFromAPI, cyclePatches]
  );

  /** Soft-deleted / archived cycles stay out of the main list (delete UX). */
  const visibleSoxCyclesForDisplay = useMemo(
    () => soxCyclesForDisplay.filter((c) => !entityIsArchived(c)),
    [soxCyclesForDisplay]
  );

  const processesForDisplay = useMemo(() => {
    const next = { ...processesFromAPI };
    for (const [k, patch] of Object.entries(processPatches)) {
      const sep = k.indexOf('|');
      if (sep === -1) continue;
      const cycleId = k.slice(0, sep);
      const processId = k.slice(sep + 1);
      const list = next[cycleId];
      if (!Array.isArray(list)) continue;
      next[cycleId] = list.map((row) => {
        const rowListKey = row.process_id ?? row.id;
        return rowListKey === processId ? { ...row, ...patch } : row;
      });
    }
    // Show processes in stored sequence order (process_order / sequence).
    for (const cycleId of Object.keys(next)) {
      next[cycleId] = sortProcessesForEndToEnd(next[cycleId]);
    }
    return next;
  }, [processesFromAPI, processPatches]);

  /** True once a cycle's processes query has successfully resolved. */
  const processLoadStateByCycleId = useMemo(() => {
    const resolvedByCycle = {};
    soxCyclesFromAPI.forEach((cycle, index) => {
      const cycleListKey = cycle.cycle_id ?? cycle.id;
      const queryState = processQueryResults[index];
      resolvedByCycle[cycleListKey] = Boolean(queryState?.isSuccess);
    });
    return resolvedByCycle;
  }, [soxCyclesFromAPI, processQueryResults]);

  /** True when a cycle's processes query has failed and should not be treated as empty data. */
  const processLoadErrorByCycleId = useMemo(() => {
    const errorByCycle = {};
    soxCyclesFromAPI.forEach((cycle, index) => {
      const cycleListKey = cycle.cycle_id ?? cycle.id;
      const queryState = processQueryResults[index];
      errorByCycle[cycleListKey] = Boolean(queryState?.isError);
    });
    return errorByCycle;
  }, [soxCyclesFromAPI, processQueryResults]);

  const {
    data: processDetailsForGenerate,
    isFetching: isFetchingProcessDetailsForGenerate,
    isPlaceholderData: isProcessDetailsPlaceholder,
  } = useProcessById(generateFlowchartTarget?.processListKey, {
    enabled: Boolean(generateFlowchartTarget),
    placeholderProcess: generateFlowchartTarget?.process ?? null,
  });
  // True while we're still waiting on the real GET /api/processes/{id} response (placeholder is still in use).
  const isLoadingProcessDetailsForGenerate =
    Boolean(generateFlowchartTarget) &&
    isFetchingProcessDetailsForGenerate &&
    isProcessDetailsPlaceholder;

  const openGenerateFlowchartModal = (process, cycleListKey) => {
    const processListKey = String(process.process_id ?? process.id ?? '').trim();
    if (!processListKey) return;
    setGenerateFlowchartTarget({ process, cycleListKey, processListKey });
  };

  const handleFlowchartGenerated = async (versionNumber) => {
    const target = generateFlowchartTarget;
    if (!target) return;
    const { cycleListKey, processListKey } = target;
    await queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleListKey) });
    setGenerateFlowchartTarget(null);
    navigate(soxProcessFlowchartPath(cycleListKey, processListKey), {
      state: mergeProcessNavigationState(
        {
          syncInitialPositions: true,
          ...(versionNumber != null ? { versionNumber } : {}),
        },
        currentPathWithSearch(location)
      ),
    });
  };

  // Derive cycle workflow status from processes (weakest link: any open → open, then in_progress, in_review, then all complete).
  const deriveCycleStatus = (processes) => {
    if (!processes || processes.length === 0) {
      return {
        status: 'open',
        isOpen: true,
        isDraft: true,
        isInProgress: false,
        isInReview: false,
        isComplete: false,
        isActive: false,
        isFlagged: false,
        isUnderReview: false
      };
    }

    const normalized = processes.map((p) =>
      normalizeSoxDashboardProcessStatus(
        String(p.workflow_status ?? p.status ?? 'open')
      )
    );

    if (normalized.some((s) => s === 'open')) {
      return {
        status: 'open',
        isOpen: true,
        isDraft: true,
        isInProgress: false,
        isInReview: false,
        isComplete: false,
        isActive: false,
        isFlagged: false,
        isUnderReview: false
      };
    }
    if (normalized.some((s) => s === 'in_progress')) {
      return {
        status: 'in_progress',
        isOpen: false,
        isDraft: false,
        isInProgress: true,
        isInReview: false,
        isComplete: false,
        isActive: true,
        isFlagged: false,
        isUnderReview: false
      };
    }
    if (normalized.some((s) => s === 'in_review')) {
      return {
        status: 'in_review',
        isOpen: false,
        isDraft: false,
        isInProgress: false,
        isInReview: true,
        isComplete: false,
        isActive: true,
        isFlagged: false,
        isUnderReview: true
      };
    }
    if (normalized.every((s) => s === 'complete')) {
      return {
        status: 'complete',
        isOpen: false,
        isDraft: false,
        isInProgress: false,
        isInReview: false,
        isComplete: true,
        isActive: true,
        isFlagged: false,
        isUnderReview: false
      };
    }
    return {
      status: 'open',
      isOpen: true,
      isDraft: true,
      isInProgress: false,
      isInReview: false,
      isComplete: false,
      isActive: false,
      isFlagged: false,
      isUnderReview: false
    };
  };

  const displayCycles = visibleSoxCyclesForDisplay.map((apiCycle) => {
    const cycleListKey = apiCycle.cycle_id ?? apiCycle.id;
    const cycleUuid = apiCycle.id ?? apiCycle.cycle_id;
    const apiProcesses = processesForDisplay[cycleListKey];
    const processCount = apiProcesses ? apiProcesses.length : 0;
    const derivedStatus = deriveCycleStatus(apiProcesses);
    const apiStatusRaw =
      apiCycle.workflow_status ??
      apiCycle.status ??
      apiCycle.dashboard_status ??
      apiCycle.cycle_status ??
      apiCycle.review_status;
    const statusFromApi = apiStatusRaw
      ? normalizeSoxDashboardProcessStatus(String(apiStatusRaw))
      : null;
    const mergedStatusFlags = statusFromApi
      ? {
          status: statusFromApi,
          isOpen: statusFromApi === 'open',
          isDraft: statusFromApi === 'open',
          isInProgress: statusFromApi === 'in_progress',
          isInReview: statusFromApi === 'in_review',
          isComplete: statusFromApi === 'complete',
          isFlagged: false,
          isUnderReview: statusFromApi === 'in_review',
          isActive:
            statusFromApi === 'in_progress' ||
            statusFromApi === 'in_review' ||
            statusFromApi === 'complete'
        }
      : derivedStatus;

    const lastReviewed =
      apiProcesses?.length > 0
        ? apiProcesses
            .map((p) => (p.last_reviewed ? new Date(p.last_reviewed) : null))
            .filter((d) => d !== null)
            .sort((a, b) => b - a)[0]
        : null;

    const formattedLastReviewed = lastReviewed
      ? lastReviewed.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : 'Never';

    const isArchivedCycle = entityIsArchived(apiCycle);
    const isActiveCycle = entityIsActive(apiCycle);

    const cycleCode =
      apiCycle.cycle_id != null ? String(apiCycle.cycle_id) : String(cycleListKey);

    /** Business identifier for request bodies (`cycle_id`) — not the UUID `id` when both exist. */
    const cycle_id = apiCycle.cycle_id != null ? String(apiCycle.cycle_id) : String(cycleListKey);

    return {
      /** Business key: GET /processes, React Query map, expansion, search */
      id: cycleListKey,
      /** UUID for PUT/PATCH /sox-cycles/{uuid}/… */
      cycleUuid,
      cycleCode,
      cycle_id,
      name: apiCycle.name ?? apiCycle.cycle_name ?? '',
      riskScore: (() => {
        const raw = apiCycle.riskScore ?? apiCycle.risk_score ?? apiCycle.riskRating;
        const n = Number.parseFloat(String(raw ?? ''));
        return Number.isFinite(n) ? n : null;
      })(),
      riskLabel: getCycleRiskLabel(
        apiCycle.riskScore ?? apiCycle.risk_score ?? apiCycle.riskRating
      ),
      processes: processCount,
      lastReviewed: formattedLastReviewed,
      owner: 'Unassigned',
      completion: 0,
      isArchivedCycle,
      /** Switch ON = active; OFF = inactive (PATCH is_active). */
      isActiveCycle,
      statusSelectValue: mergedStatusFlags.status,
      ...mergedStatusFlags
    };
  });

  const filteredDisplayCycles = useMemo(() => {
    return displayCycles
      .filter((cycle) => {
        if (statusFilter === 'all') return true;
        const normalized = normalizeSoxDashboardProcessStatus(cycle.status);
        return normalized === statusFilter;
      })
      .filter((cycle) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        if ((cycle.name || '').toLowerCase().includes(query)) return true;
        if (String(cycle.cycleCode).toLowerCase().includes(query)) return true;
        if (String(cycle.id).toLowerCase().includes(query)) return true;
        const cycleProcesses = processesForDisplay[cycle.id] || [];
        return cycleProcesses.some((process) => {
          const pid =
            process.process_id != null ? String(process.process_id) : String(process.id ?? '');
          return (
            process.name.toLowerCase().includes(query) ||
            pid.toLowerCase().includes(query) ||
            (process.description && process.description.toLowerCase().includes(query))
          );
        });
      });
  }, [displayCycles, statusFilter, searchQuery, processesForDisplay]);

  const selectedCycleKeySet = useMemo(() => new Set(selectedCycles), [selectedCycles]);
  const visibleCycleRowKeys = useMemo(
    () => filteredDisplayCycles.map((c) => c.id).filter(Boolean),
    [filteredDisplayCycles]
  );
  const allVisibleCyclesSelected = useMemo(
    () =>
      visibleCycleRowKeys.length > 0 &&
      visibleCycleRowKeys.every((k) => selectedCycleKeySet.has(k)),
    [visibleCycleRowKeys, selectedCycleKeySet]
  );
  const toggleAllVisibleCycles = useCallback(
    (checked) => {
      if (checked) {
        setSelectedCycles((prev) => {
          const next = new Set(prev);
          visibleCycleRowKeys.forEach((k) => next.add(k));
          return Array.from(next);
        });
      } else {
        const visibleSet = new Set(visibleCycleRowKeys);
        setSelectedCycles((prev) => prev.filter((k) => !visibleSet.has(k)));
      }
    },
    [visibleCycleRowKeys]
  );
  const toggleCycleKey = useCallback((key, checked) => {
    setSelectedCycles((prev) =>
      checked ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((x) => x !== key)
    );
  }, []);

  const handleDeleteSelectedCycles = async () => {
    if (selectedCycles.length === 0) return;
    if (
      !window.confirm(`Are you sure you want to delete ${selectedCycles.length} cycle(s)?`)
    ) {
      return;
    }

    const selectedData = filteredDisplayCycles.filter((c) => selectedCycleKeySet.has(c.id));
    if (selectedData.length === 0) {
      alert(
        'No selected cycles match the current filters. Clear your selection or adjust filters.'
      );
      return;
    }
    if (selectedData.length !== selectedCycles.length) {
      const proceed = window.confirm(
        `${selectedCycles.length} cycle(s) are selected, but only ${selectedData.length} match the current filters. Delete only those?`
      );
      if (!proceed) return;
    }

    setDeletingSelectedCycles(true);
    try {
      for (const cycle of selectedData) {
        if (cycle.cycleUuid) {
          await patchSoxCycleArchive(cycle.cycleUuid);
        }
      }
      const deletedKeys = new Set(selectedData.map((c) => c.id));
      setSelectedCycles([]);
      setExpandedCycles((prev) => prev.filter((id) => !deletedKeys.has(id)));
      await queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY });
      toast.success(
        selectedData.length === 1 ? 'Cycle deleted' : `${selectedData.length} cycles deleted`
      );
    } catch (err) {
      console.error('Error deleting cycles:', err);
      toast.error(resolve403ErrorMessage(err, 'Failed to delete cycles. Please try again.'));
      await queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY });
    } finally {
      setDeletingSelectedCycles(false);
    }
  };

  // Calculate stats based on actual cycle data
  const calculateStats = () => {
    const totalCycles = displayCycles.length;

    const completedCycles = displayCycles.filter((c) => c.status === 'complete').length;
    const inProgressCycles = displayCycles.filter(
      (c) => c.status === 'in_progress' || c.status === 'in_review'
    ).length;
    const openCycles = displayCycles.filter((c) => c.status === 'open').length;

    const completedPercentage = totalCycles > 0 ? Math.round((completedCycles / totalCycles) * 100) : 0;
    const inProgressPercentage = totalCycles > 0 ? Math.round((inProgressCycles / totalCycles) * 100) : 0;
    const openPercentage = totalCycles > 0 ? Math.round((openCycles / totalCycles) * 100) : 0;

    return {
      totalCycles,
      completedCycles,
      completedPercentage,
      inProgressCycles,
      inProgressPercentage,
      openCycles,
      openPercentage
    };
  };

  const stats = calculateStats();

  /** Switch reflects pending PATCH so UI updates immediately. */
  const getCycleSwitchActive = (cycle) => {
    const m = patchCycleActiveStatusMutation;
    if (m.isPending && m.variables?.cycleListKey === cycle.id) {
      return m.variables?.is_active;
    }
    return cycle.isActiveCycle;
  };

  const getProcessSwitchActive = (cycleListKey, processUuid, resolvedActive) => {
    const m = patchProcessActiveStatusMutation;
    if (
      m.isPending &&
      m.variables?.cycleListKey === cycleListKey &&
      m.variables?.processUuid === processUuid
    ) {
      return m.variables?.is_active;
    }
    return resolvedActive;
  };

  // Toggle cycle expansion
  const toggleCycleExpansion = (cycleId) => {
    setExpandedCycles(prev =>
      prev.includes(cycleId)
        ? prev.filter(id => id !== cycleId)
        : [...prev, cycleId]
    );
  };

  const moveProcessInCycle = useCallback(
    (cycleListKey, processUuid, direction) => {
      const ordered = sortProcessesForEndToEnd(processesForDisplay[cycleListKey] || []);
      const index = ordered.findIndex(
        (process) => String(process?.id ?? process?.process_id) === String(processUuid)
      );
      if (index < 0) return;
      const swapWith = direction === 'up' ? index - 1 : index + 1;
      if (swapWith < 0 || swapWith >= ordered.length) return;

      const next = [...ordered];
      const temp = next[index];
      next[index] = next[swapWith];
      next[swapWith] = temp;

      const processUuids = next
        .map((process) => String(process?.id ?? '').trim())
        .filter(Boolean);
      if (processUuids.length !== next.length) {
        toast.error('Could not update process sequence');
        return;
      }

      reorderCycleProcessesMutation.mutate({
        cycleListKey,
        processUuids,
      });
    },
    [processesForDisplay, reorderCycleProcessesMutation]
  );

  const processDragRef = useRef({ cycleListKey: null, fromIndex: null });
  const [processDragOver, setProcessDragOver] = useState(null);
  const processDragAutoScrollRef = useRef({
    active: false,
    clientY: 0,
    rafId: null,
    onDragOver: null,
  });

  const stopProcessDragAutoScroll = useCallback(() => {
    const state = processDragAutoScrollRef.current;
    state.active = false;
    if (state.rafId != null) {
      window.cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
    if (state.onDragOver) {
      window.removeEventListener('dragover', state.onDragOver);
      state.onDragOver = null;
    }
  }, []);

  const startProcessDragAutoScroll = useCallback(
    (clientY) => {
      const state = processDragAutoScrollRef.current;
      stopProcessDragAutoScroll();
      state.active = true;
      state.clientY = Number(clientY) || 0;

      const onDragOver = (event) => {
        state.clientY = event.clientY;
      };
      state.onDragOver = onDragOver;
      window.addEventListener('dragover', onDragOver);

      const edgePx = 80;
      const maxSpeed = 28;

      const tick = () => {
        if (!state.active) return;
        const y = state.clientY;
        const viewportHeight = window.innerHeight || 0;
        let deltaY = 0;
        if (y < edgePx) {
          deltaY = -Math.ceil(((edgePx - y) / edgePx) * maxSpeed);
        } else if (y > viewportHeight - edgePx) {
          deltaY = Math.ceil(((y - (viewportHeight - edgePx)) / edgePx) * maxSpeed);
        }
        if (deltaY !== 0) {
          window.scrollBy(0, deltaY);
        }
        state.rafId = window.requestAnimationFrame(tick);
      };

      state.rafId = window.requestAnimationFrame(tick);
    },
    [stopProcessDragAutoScroll]
  );

  useEffect(() => () => stopProcessDragAutoScroll(), [stopProcessDragAutoScroll]);

  const reorderProcessToIndex = useCallback(
    (cycleListKey, fromIndex, toIndex) => {
      const ordered = sortProcessesForEndToEnd(processesForDisplay[cycleListKey] || []);
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= ordered.length ||
        toIndex >= ordered.length
      ) {
        return;
      }
      const next = [...ordered];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      const processUuids = next
        .map((process) => String(process?.id ?? '').trim())
        .filter(Boolean);
      if (processUuids.length !== next.length) {
        toast.error('Could not update process sequence');
        return;
      }
      reorderCycleProcessesMutation.mutate({
        cycleListKey,
        processUuids,
      });
    },
    [processesForDisplay, reorderCycleProcessesMutation]
  );

  const handleAddNewCycle = () => {
    setShowAddCycleModal(true);
    setCycleSelection('');
    setCustomCycleId('');
    setCustomCycleName('');
    setCycleError('');
    setNewCycleRiskScore(5);
  };

  const handleCloseCycleModal = () => {
    setShowAddCycleModal(false);
    setCycleSelection('');
    setCustomCycleId('');
    setCustomCycleName('');
    setCycleError('');
    setIsSavingCycle(false);
    setNewCycleRiskScore(5);
  };

  const openEditCycleRiskModal = (cycle) => {
    if (!cycle?.cycleUuid) {
      toast.error('Could not resolve cycle for risk score update.');
      return;
    }
    const raw = cycle.riskScore;
    const initial =
      Number.isFinite(raw) && raw >= 1 && raw <= 10 ? raw : 5;
    setEditCycleRiskScore(initial);
    setEditCycleRiskError('');
    setEditCycleRiskTarget({
      cycleListKey: cycle.id,
      cycleUuid: cycle.cycleUuid,
      cycleName: cycle.name || cycle.cycleCode || 'Cycle'
    });
  };

  const handleCloseEditCycleRiskModal = () => {
    if (patchCycleRiskScoreMutation.isPending) return;
    setEditCycleRiskTarget(null);
    setEditCycleRiskError('');
    setEditCycleRiskScore(5);
  };

  const handleSaveEditCycleRisk = () => {
    if (!editCycleRiskTarget?.cycleUuid) return;
    const parsed = parseMaterialityRiskScoreForPayload(editCycleRiskScore);
    if (!parsed.ok) {
      setEditCycleRiskError(parsed.message);
      return;
    }
    setEditCycleRiskError('');
    patchCycleRiskScoreMutation.mutate(
      {
        cycleListKey: editCycleRiskTarget.cycleListKey,
        cycleUuid: editCycleRiskTarget.cycleUuid,
        riskScore: parsed.value
      },
      {
        onSuccess: () => {
          setEditCycleRiskTarget(null);
          setEditCycleRiskError('');
          setEditCycleRiskScore(5);
        }
      }
    );
  };

  const handleSaveNewCycle = async () => {
    // Validate input
    if (!cycleSelection) {
      setCycleError('Please select a cycle type');
      return;
    }

    let cycleId, cycleName;
     if (cycleSelection === 'custom') {
      if (!customCycleId.trim() || !customCycleName.trim()) {
        setCycleError('Please provide both Cycle ID and Cycle Name');
        return;
      }
      cycleId = customCycleId.trim().toUpperCase();
      cycleName = customCycleName.trim();
    } else {
      const selectedCycle = standardCyclesForModal.find((c) => c.id === cycleSelection);
      if (!selectedCycle) {
        setCycleError('Invalid cycle type selected');
        return;
      }
      cycleId = selectedCycle.id;
      cycleName = selectedCycle.name;
    }


    const parsedRiskScore = parseMaterialityRiskScoreForPayload(newCycleRiskScore);
    if (!parsedRiskScore.ok) {
      setCycleError(parsedRiskScore.message);
      return;
    }

    try {
      setIsSavingCycle(true);
      setCycleError('');

      const payload = {
        cycleId,
        name: cycleName,
        riskScore: parsedRiskScore.value,
        ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
      };

      const response = await axiosInstance.post('/api/sox-cycles', payload);

      if (response.data.success) {
        await queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY });
        handleCloseCycleModal();

        const savedCycleId = response.data?.cycle?.cycle_id || cycleId;
        if (response.data?.cycle?.cycle_id_was_deduped) {
          toast.success(
            `Saved as ${savedCycleId} — "${response.data.cycle.requested_cycle_id}" was already in use.`
          );
        } else {
          toast.success(`Cycle "${cycleName}" (${savedCycleId}) created`);
        }

        navigate(soxProcessesListPath(savedCycleId), {
          state: buildProcessReturnState(currentPathWithSearch(location)),
        });
      } else {
        setCycleError(
          response.data?.message || response.data?.error || 'Could not create cycle. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error saving cycle:', error);
      setCycleError(
        resolve403ErrorMessage(
          error,
          error.response?.data?.message || error.response?.data?.error || 'Failed to save cycle. Please try again.'
        )
      );
    } finally {
      setIsSavingCycle(false);
    }
  };


  const handleContinue = () => {
    if (currentStep === 2) {
      if (!selectedCycleForSetup?.id) {
        alert('Please select a cycle first');
        return;
      }
      const tab = flowchartMethod === 'upload' ? 'upload' : flowchartMethod === 'ai' ? 'ai' : 'manual';
      navigate(
        soxProcessCreatePath(selectedCycleForSetup.cycle_id ?? selectedCycleForSetup.id),
        {
          state: mergeProcessNavigationState({ tab }, currentPathWithSearch(location)),
        }
      );
    }
  };

  const handleBack = async () => {
    if (showAIDesigner) {
      setShowAIDesigner(false);
      if (selectedCycleForSetup?.id) {
        const cycleId = selectedCycleForSetup.id;
        await queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) });
      }
    } else {
      setShowSetupFlow(false);
      setCurrentStep(2);
      setSelectedProcess(null);
      setSelectedCycleForSetup(null);
    }
  };

  const handleCompleteSetup = async () => {
    if (selectedCycleForSetup?.id) {
      const cycleId = selectedCycleForSetup.id;
      await queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) });
      await queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY });
    }

    // Reset all state
    setShowSetupFlow(false);
    setShowAIDesigner(false);
    setCurrentStep(2);
    setSelectedProcess(null);
    setSelectedCycleForSetup(null);
  };

  // AI Designer View
  if (showAIDesigner) {
    return (
      <AISOXProcessDesigner
        selectedProcess={selectedProcess}
        cycle_id={selectedCycleForSetup?.cycle_id ?? selectedCycleForSetup?.cycleCode ?? selectedCycleForSetup?.id}
        cycleId={selectedCycleForSetup?.cycle_id ?? selectedCycleForSetup?.cycleCode ?? selectedCycleForSetup?.id}
        cycleUuid={selectedCycleForSetup?.cycleUuid}
        cycleName={selectedCycleForSetup?.name}
        onBack={handleBack}
        onComplete={handleCompleteSetup}
      />
    );
  }

  // Setup Flow View
  if (showSetupFlow) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleBack}
                className="text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div>
                <h2 className="text-xl font-semibold">{selectedCycleForSetup?.name || 'Setup New SOX Cycle'}</h2>
                <p className="text-sm text-gray-500">Design the process for the cycle: flowchart, risks, and controls</p>
              </div>
            </div>
          </div>

          {currentStep === 2 && (
            <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <h3 className="text-2xl font-bold mb-2 text-gray-900">How would you like to create your process?</h3>
              <p className="text-gray-600 mb-8">Choose the method that best fits your workflow</p>

              {/* Featured: AI Process Designer */}
              <div
                onClick={() => setFlowchartMethod('ai')}
                className={`relative bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-8 cursor-pointer transition-all hover:shadow-2xl mb-6 overflow-hidden ${
                  flowchartMethod === 'ai' ? 'ring-4 ring-blue-300' : ''
                }`}
              >
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>

                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-white rounded-xl shadow-lg">
                        <Bot className="h-10 w-10 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-2xl font-bold text-white">AI Process Designer</h4>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400 text-gray-900">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Recommended
                          </span>
                        </div>
                        <p className="text-blue-100">Powered by advanced AI technology</p>
                      </div>
                    </div>
                    {flowchartMethod === 'ai' && (
                      <div className="bg-white rounded-full p-2">
                        <Check className="h-6 w-6 text-blue-600" />
                      </div>
                    )}
                  </div>

                  <p className="text-white text-base mb-6 max-w-3xl">
                    Automatically transform process walkthrough recordings, narratives, or descriptions into professional flowcharts. Our AI understands your process and creates accurate, SOX-compliant documentation.
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <FileText className="h-5 w-5 text-white" />
                        <span className="text-sm font-semibold text-white">Upload Recordings</span>
                      </div>
                      <p className="text-xs text-blue-100">Upload meeting recordings or process walkthroughs</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Sparkles className="h-5 w-5 text-white" />
                        <span className="text-sm font-semibold text-white">Real-time Feedback</span>
                      </div>
                      <p className="text-xs text-blue-100">Interactive design with visual feedback</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                      <Check className="h-3 w-3 mr-1" />
                      Auto SOX control identification
                    </span>
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                      <Check className="h-3 w-3 mr-1" />
                      AI generated risks and recommendations
                    </span>
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                      <Check className="h-3 w-3 mr-1" />
                      Iterative refinement
                    </span>
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                      <Check className="h-3 w-3 mr-1" />
                      Side-by-side conversation
                    </span>
                  </div>
                </div>
              </div>

              {/* Other Options */}
              <div className="grid grid-cols-2 gap-6">
                {/* Upload File Option */}
                <div
                  onClick={() => setFlowchartMethod('upload')}
                  className={`bg-white border-2 rounded-lg p-6 cursor-pointer transition-all ${
                    flowchartMethod === 'upload'
                      ? 'border-blue-500 ring-2 ring-blue-100'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Upload className="h-6 w-6 text-blue-600" />
                    </div>
                    {flowchartMethod === 'upload' && <Check className="h-5 w-5 text-blue-600" />}
                  </div>
                  <h4 className="font-semibold text-lg mb-2 text-gray-900">Upload File</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a process template file and create processes for this cycle.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span>Supports Excel (.xlsx) and CSV (.csv)</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-blue-600" />
                      <span>Validate rows before saving</span>
                    </div>
                  </div>
                </div>

                {/* Create Manually Option */}
                <div
                  onClick={() => setFlowchartMethod('manual')}
                  className={`bg-white border-2 rounded-lg p-6 cursor-pointer transition-all ${
                    flowchartMethod === 'manual'
                      ? 'border-green-500 ring-2 ring-green-100'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileText className="h-6 w-6 text-green-600" />
                    </div>
                    {flowchartMethod === 'manual' && <Check className="h-5 w-5 text-green-600" />}
                  </div>
                  <h4 className="font-semibold text-lg mb-2 text-gray-900">Create Manually</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Build the process step by step yourself when you want full control over the flow.
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600" />
                      <span>Add custom process steps</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600" />
                      <span>Define risks and controls manually</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation button */}
              <div className="flex justify-end mt-8">
                <button
                  onClick={handleContinue}
                  className="px-8 py-3 rounded-lg flex items-center space-x-2 font-medium bg-gray-900 text-white hover:bg-gray-800"
                >
                  <span>Create Process</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
          opacity: 0;
        }
        
        .animate-fadeInLeft {
          animation: fadeInLeft 0.5s ease-out forwards;
          opacity: 0;
        }
        
        .animate-fadeInRight {
          animation: fadeInRight 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
      <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">SOX Cycles Dashboard</h1>
              <p className="text-sm text-gray-500">Monitor and manage your SOX compliance cycles</p>
            </div>
            <div className="flex items-center space-x-3">
              <ModuleAuditDownloadButton
                resourceType="sox_cycle"
                moduleKey={MODULE_KEYS.PROCESSES}
                filenamePrefix="sox_cycle_audit"
                label="Download Cycle Audit"
              />
              <ModuleAuditDownloadButton
                resourceType="process"
                moduleKey={MODULE_KEYS.PROCESSES}
                filenamePrefix="process_audit"
                label="Download Process Audit"
              />
              <button
                onClick={handleAddNewCycle}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-800 transition-all hover:scale-105 duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Sox Cycle
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="bg-white rounded-lg shadow p-6 transition-all hover:shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">Total SOX Cycles</span>
              <Activity className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">{stats.totalCycles}</span>
            </div>
            <span className="text-xs text-gray-500">All cycles in system</span>
          </div>

          <div className="bg-white rounded-lg shadow p-6 transition-all hover:shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">Setup Completed</span>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">{stats.completedCycles}</span>
              <span className="ml-2 text-lg font-semibold text-green-600">({stats.completedPercentage}%)</span>
            </div>
            <span className="text-xs text-gray-500">Workflow status = complete</span>
          </div>

          <div className="bg-white rounded-lg shadow p-6 transition-all hover:shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">In Progress / Review</span>
              <Activity className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">{stats.inProgressCycles}</span>
              <span className="ml-2 text-lg font-semibold text-blue-600">({stats.inProgressPercentage}%)</span>
            </div>
            <span className="text-xs text-gray-500">In Progress or in Review</span>
          </div>

          <div className="bg-white rounded-lg shadow p-6 transition-all hover:shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">Open</span>
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">{stats.openCycles}</span>
              <span className="ml-2 text-lg font-semibold text-amber-600">({stats.openPercentage}%)</span>
            </div>
            <span className="text-xs text-gray-500">Workflow status = open</span>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex items-center gap-4 mb-6 animate-fadeInUp" style={{ animationDelay: '0.15s' }}>
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search processes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            />
          </div>

          {/* Status Filter — all workflow statuses + All */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 min-w-0">
            <span className="text-sm font-medium text-gray-600 whitespace-nowrap shrink-0">
              Status:
            </span>
            <div className="flex flex-wrap items-center gap-1 rounded-lg bg-gray-100 p-1 min-w-0">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All
              </button>
              {SOX_DASHBOARD_STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    statusFilter === opt.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* SOX Cycles Cards */}
        {loadingCycles ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <Loader className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-500">Loading SOX cycles...</p>
          </div>
        ) : displayCycles.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No SOX cycles found</h3>
            <p className="text-sm text-gray-500 mb-6">Get started by adding your first SOX cycle to begin mapping processes and controls.</p>
            <button
              onClick={handleAddNewCycle}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-all"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Sox Cycle
            </button>
          </div>
        ) : (
        <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <div
          className="bg-white rounded-lg shadow border border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between gap-3"
        >
          {selectedCycles.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-blue-900">
                {selectedCycles.length}{' '}
                {selectedCycles.length === 1 ? 'item' : 'items'} selected of{' '}
                {displayCycles.length} {displayCycles.length === 1 ? 'cycle' : 'cycles'}
              </span>
              <button
                type="button"
                onClick={handleDeleteSelectedCycles}
                disabled={deletingSelectedCycles}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                aria-label="Delete selected cycles"
              >
                {deletingSelectedCycles ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setSelectedCycles([])}
                disabled={deletingSelectedCycles}
                className="p-1 text-blue-700 hover:text-blue-900 transition-colors disabled:opacity-50"
                aria-label="Cancel selections"
                title="Cancel selections"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                onChange={(e) => toggleAllVisibleCycles(e.target.checked)}
                checked={allVisibleCyclesSelected}
                disabled={loadingCycles || visibleCycleRowKeys.length === 0}
                aria-label="Select all cycles"
              />
              <h2 className="text-lg font-medium text-gray-900">
                All SOX Cycles ({filteredDisplayCycles.length})
              </h2>
            </div>
          )}
        </div>
          {filteredDisplayCycles
            .map((cycle, index) => {
            const isExpanded = expandedCycles.includes(cycle.id);
            // Use API data only
            const cycleProcesses = processesForDisplay[cycle.id] || [];
            const hasResolvedCycleProcesses = processLoadStateByCycleId[cycle.id] === true;
            const hasProcessLoadError = processLoadErrorByCycleId[cycle.id] === true;
            const cycleHasProcesses = hasResolvedCycleProcesses && cycleProcesses.length > 0;
            const cycleSetupActionLabel = hasProcessLoadError
              ? 'Retry Loading'
              : hasResolvedCycleProcesses
              ? 'Configure Processes'
              : 'Loading…';
            const handleCycleSetupAction = () => {
              if (hasProcessLoadError) {
                queryClient.invalidateQueries({ queryKey: processesQueryKey(cycle.id) });
                return;
              }
              if (!hasResolvedCycleProcesses) return;
              navigate(soxProcessesListPath(cycle.cycle_id ?? cycle.id), {
                state: buildProcessReturnState(currentPathWithSearch(location)),
              });
            };

            const cycleRowBusy =
              (patchCycleWorkflowStatusMutation.isPending &&
                patchCycleWorkflowStatusMutation.variables?.cycleListKey === cycle.id) ||
              (patchCycleRiskScoreMutation.isPending &&
                patchCycleRiskScoreMutation.variables?.cycleListKey === cycle.id) ||
              (patchCycleActiveStatusMutation.isPending &&
                patchCycleActiveStatusMutation.variables?.cycleListKey === cycle.id) ||
              (archiveCycleMutation.isPending &&
                archiveCycleMutation.variables?.cycleListKey === cycle.id) ||
              (unarchiveCycleMutation.isPending &&
                unarchiveCycleMutation.variables?.cycleListKey === cycle.id);

            return (
              <div
                key={cycle.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm animate-fadeInUp"
                style={{ animationDelay: `${0.3 + index * 0.05}s` }}
              >
                <div className="px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4">
                  <input
                    type="checkbox"
                    className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    checked={Boolean(cycle.id) && selectedCycleKeySet.has(cycle.id)}
                    onChange={(e) => {
                      if (!cycle.id) return;
                      toggleCycleKey(cycle.id, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={deletingSelectedCycles}
                    aria-label={`Select ${cycle.name}`}
                  />
                  <button
                    type="button"
                    onClick={() => toggleCycleExpansion(cycle.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4 text-left hover:opacity-90"
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-sky-100">
                      <span className="text-xs font-bold tracking-wide text-sky-900">
                        {cycle.cycleCode}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{cycle.name}</h3>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                        <span>Last Reviewed: {cycle.lastReviewed}</span>
                        <span className="text-gray-300">·</span>
                        <span>
                          {typeof cycle.processes === 'number'
                            ? `${cycle.processes} processes`
                            : cycle.processes}
                        </span>
                      </div>
                    </div>
                  </button>

                  <div
                    className="flex flex-shrink-0 items-center gap-2 sm:gap-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => openEditCycleRiskModal(cycle)}
                      disabled={cycleRowBusy || !cycle.cycleUuid}
                      className={`inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium disabled:opacity-50 ${getCycleRiskLabelClasses(
                        cycle.riskLabel
                      )}`}
                      aria-label={`Edit cycle risk: ${cycle.riskLabel || 'Not assessed'}`}
                      title="Adjust risk score"
                    >
                      {cycle.riskLabel || 'Not Assessed'}
                      <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                    </button>
                    <button
                      type="button"
                      onClick={handleCycleSetupAction}
                      disabled={!hasResolvedCycleProcesses && !hasProcessLoadError}
                      className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-800 shadow-sm hover:bg-gray-50"
                    >
                      <FolderInput className="h-3.5 w-3.5 text-gray-600" />
                      {cycleSetupActionLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!hasResolvedCycleProcesses) return;
                        navigate(soxCycleEndToEndFlowchartPath(cycle.cycle_id ?? cycle.id), {
                          state: buildProcessReturnState(currentPathWithSearch(location)),
                        });
                      }}
                      disabled={!hasResolvedCycleProcesses}
                      className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-800 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <GitMerge className="h-3.5 w-3.5 text-gray-600" />
                      End to end flowchart
                    </button>
                    <StatusSelect
                      options={SOX_DASHBOARD_STATUS_OPTIONS}
                      value={normalizeSoxDashboardProcessStatus(cycle.statusSelectValue)}
                      disabled={cycleRowBusy}
                      className="min-w-[7.5rem]"
                      aria-label={`Cycle workflow status: ${cycle.name}`}
                      onChange={(next) => {
                        if (
                          next ===
                          normalizeSoxDashboardProcessStatus(cycle.statusSelectValue)
                        ) {
                          return;
                        }
                        patchCycleWorkflowStatusMutation.mutate({
                          cycleListKey: cycle.id,
                          cycleUuid: cycle.cycleUuid,
                          status: next
                        });
                      }}
                    />

                    <ActiveSwitch
                      active={getCycleSwitchActive(cycle)}
                      disabled={cycleRowBusy}
                      ariaLabel={`${cycle.name}: active when on, inactive when off`}
                      onToggle={(wantActive) => {
                        patchCycleActiveStatusMutation.mutate({
                          cycleListKey: cycle.id,
                          cycleUuid: cycle.cycleUuid,
                          is_active: wantActive
                        });
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleCycleSetupAction}
                      disabled={!hasResolvedCycleProcesses && !hasProcessLoadError}
                      className="inline-flex sm:hidden items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-800 shadow-sm hover:bg-gray-50"
                      title={cycleSetupActionLabel}
                    >
                      <FolderInput className="h-4 w-4 text-gray-600" />
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setCycleArchiveConfirm({
                          cycleListKey: cycle.id,
                          cycleUuid: cycle.cycleUuid,
                          name: cycle.name,
                          isArchived: cycle.isArchivedCycle
                        })
                      }
                      disabled={cycleRowBusy}
                      className="inline-flex items-center justify-center rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title={cycle.isArchivedCycle ? 'Restore cycle' : 'Delete cycle'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleCycleExpansion(cycle.id)}
                      className="inline-flex items-center justify-center rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse cycle' : 'Expand cycle'}
                    >
                      <ChevronDown
                        className={`h-5 w-5 transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50">
                    {cycleProcesses.length === 0 ? (
                      <div className="px-6 py-8 text-center text-sm text-gray-500">
                        No processes in this cycle yet. Use Configure Processes to add processes.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                Process
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                Owner
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                Last Reviewed
                              </th>
                              <th className="px-4 py-3 w-[1%]" aria-hidden="true" />
                              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                Status
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {cycleProcesses.map((process, processIndex) => {
                              /** UUID for PUT/PATCH/DELETE /processes/{uuid}/… */
                              const processUuid = process.id ?? process.process_id;
                              /** Business key for list matching and non-UUID routes */
                              const processListKey = process.process_id ?? process.id;
                              const processCode =
                                process.process_id != null
                                  ? String(process.process_id)
                                  : String(processUuid);
                              const processWorkflowRaw =
                                process.workflow_status ?? process.status ?? '';
                              const lastReviewed = process.last_reviewed
                                ? new Date(process.last_reviewed).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : process.lastReviewed || 'Never';
                              const processBusy =
                                (patchProcessWorkflowStatusMutation.isPending &&
                                  patchProcessWorkflowStatusMutation.variables?.processUuid ===
                                    processUuid &&
                                  patchProcessWorkflowStatusMutation.variables?.cycleListKey ===
                                    cycle.id) ||
                                (patchProcessActiveStatusMutation.isPending &&
                                  patchProcessActiveStatusMutation.variables?.processUuid ===
                                    processUuid &&
                                  patchProcessActiveStatusMutation.variables?.cycleListKey ===
                                    cycle.id) ||
                                (deleteProcessMutation.isPending &&
                                  deleteProcessMutation.variables?.processUuid === processUuid) ||
                                (reorderCycleProcessesMutation.isPending &&
                                  reorderCycleProcessesMutation.variables?.cycleListKey === cycle.id);
                              const processActive = entityIsActive(process);
                              const processArchived = entityIsArchived(process);
                              const canMoveUp = processIndex > 0;
                              const canMoveDown = processIndex < cycleProcesses.length - 1;

                              return (
                                <tr
                                  key={processUuid}
                                  className={`hover:bg-gray-50/80 ${
                                    processDragOver?.cycleListKey === cycle.id &&
                                    processDragOver?.index === processIndex
                                      ? 'bg-sky-50 ring-1 ring-inset ring-sky-200'
                                      : ''
                                  }`}
                                  onDragOver={(event) => {
                                    if (processBusy) return;
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = 'move';
                                    setProcessDragOver({
                                      cycleListKey: cycle.id,
                                      index: processIndex,
                                    });
                                  }}
                                  onDragLeave={() => {
                                    setProcessDragOver((current) => {
                                      if (
                                        current?.cycleListKey === cycle.id &&
                                        current?.index === processIndex
                                      ) {
                                        return null;
                                      }
                                      return current;
                                    });
                                  }}
                                  onDrop={(event) => {
                                    event.preventDefault();
                                    const drag = processDragRef.current;
                                    setProcessDragOver(null);
                                    stopProcessDragAutoScroll();
                                    if (
                                      !drag ||
                                      drag.cycleListKey !== cycle.id ||
                                      drag.fromIndex == null ||
                                      processBusy
                                    ) {
                                      return;
                                    }
                                    reorderProcessToIndex(
                                      cycle.id,
                                      drag.fromIndex,
                                      processIndex
                                    );
                                    processDragRef.current = {
                                      cycleListKey: null,
                                      fromIndex: null,
                                    };
                                  }}
                                >
                                  <td className="max-w-xs px-4 py-4 align-middle">
                                    <div className="flex items-start gap-2">
                                      <div className="flex shrink-0 flex-col items-center gap-0.5 pt-0.5">
                                        <button
                                          type="button"
                                          draggable={!processBusy}
                                          onDragStart={(event) => {
                                            processDragRef.current = {
                                              cycleListKey: cycle.id,
                                              fromIndex: processIndex,
                                            };
                                            event.dataTransfer.effectAllowed = 'move';
                                            try {
                                              event.dataTransfer.setData(
                                                'text/plain',
                                                String(processUuid)
                                              );
                                            } catch {
                                              // Some browsers require setData for drag to start.
                                            }
                                            startProcessDragAutoScroll(event.clientY);
                                          }}
                                          onDragEnd={() => {
                                            processDragRef.current = {
                                              cycleListKey: null,
                                              fromIndex: null,
                                            };
                                            setProcessDragOver(null);
                                            stopProcessDragAutoScroll();
                                          }}
                                          disabled={processBusy}
                                          className="inline-flex h-6 w-6 cursor-grab items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label={`Drag to reorder ${process.name}`}
                                          title="Drag to reorder"
                                        >
                                          <GripVertical className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            moveProcessInCycle(cycle.id, processUuid, 'up')
                                          }
                                          disabled={processBusy || !canMoveUp}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label={`Move ${process.name} up`}
                                          title="Move up"
                                        >
                                          <ChevronUp className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            moveProcessInCycle(cycle.id, processUuid, 'down')
                                          }
                                          disabled={processBusy || !canMoveDown}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                                          aria-label={`Move ${process.name} down`}
                                          title="Move down"
                                        >
                                          <ChevronDown className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                      <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-900">
                                          {process.name}
                                        </div>
                                        <div className="mt-0.5 text-xs font-medium tracking-wide text-gray-600">
                                          {processCode}
                                        </div>
                                        <div className="mt-1 text-xs leading-relaxed text-gray-500">
                                          {process.description || '—'}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 align-middle text-sm text-gray-900">
                                    {process.owner || 'Unassigned'}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 align-middle text-sm text-gray-900">
                                    {lastReviewed}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 align-middle">
                                    {process.has_flowchart ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          navigate(
                                            soxProcessFlowchartPath(
                                              cycle.cycle_id ?? cycle.id,
                                              processListKey
                                            ),
                                            { state: buildProcessReturnState(currentPathWithSearch(location)) }
                                          )
                                        }
                                        className={flowchartActionButtonClass}
                                      >
                                        <FileText className="h-3.5 w-3.5 text-gray-600" />
                                        Edit Flowchart
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => openGenerateFlowchartModal(process, cycle.id)}
                                        className={flowchartActionButtonClass}
                                      >
                                        <FileText className="h-3.5 w-3.5 text-gray-600" />
                                        Generate Flowchart
                                      </button>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 align-middle">
                                    {(() => {
                                      const statusValue =
                                        normalizeSoxDashboardProcessStatus(processWorkflowRaw);
                                      const statusLabel =
                                        SOX_DASHBOARD_STATUS_OPTIONS.find(
                                          (option) => option.value === statusValue
                                        )?.label ?? 'Open';
                                      const tone = getSoxDashboardStatusPillTone(statusValue);
                                      return (
                                        <span
                                          className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium"
                                          style={{
                                            backgroundColor: tone.backgroundColor,
                                            borderColor: tone.borderColor,
                                            color: tone.color,
                                          }}
                                          aria-label={`Process workflow status: ${process.name}`}
                                        >
                                          {statusLabel}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-4 align-middle">
                                    <div className="flex items-center justify-end gap-2">
                                      <ActiveSwitch
                                        active={getProcessSwitchActive(
                                          cycle.id,
                                          processUuid,
                                          processActive
                                        )}
                                        disabled={processBusy}
                                        ariaLabel={`${process.name}: active when on, inactive when off`}
                                        onToggle={(wantActive) => {
                                          patchProcessActiveStatusMutation.mutate({
                                            cycleListKey: cycle.id,
                                            processListKey,
                                            processUuid,
                                            is_active: wantActive
                                          });
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setProcessArchiveConfirm({
                                            cycleListKey: cycle.id,
                                            processListKey,
                                            processUuid,
                                            processName: process.name,
                                            isArchived: processArchived
                                          })
                                        }
                                        disabled={processBusy}
                                        className="inline-flex items-center justify-center rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        title={
                                          processArchived ? 'Restore process from archive' : 'Archive process'
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {cycleArchiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {cycleArchiveConfirm.isArchived ? 'Restore cycle' : 'Delete cycle'}
            </h3>
            <p className="mt-3 text-sm text-gray-600">
              {cycleArchiveConfirm.isArchived
                ? `Put "${cycleArchiveConfirm.name}" back on your dashboard?`
                : `Delete "${cycleArchiveConfirm.name}"? It will be removed from your dashboard.`}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setCycleArchiveConfirm(null)}
                disabled={
                  archiveCycleMutation.isPending || unarchiveCycleMutation.isPending
                }
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const { cycleListKey, cycleUuid, isArchived } = cycleArchiveConfirm;
                  const payload = { cycleListKey, cycleUuid };
                  if (isArchived) {
                    unarchiveCycleMutation.mutate(payload, {
                      onSettled: () => setCycleArchiveConfirm(null)
                    });
                  } else {
                    archiveCycleMutation.mutate(payload, {
                      onSettled: () => setCycleArchiveConfirm(null)
                    });
                  }
                }}
                disabled={
                  archiveCycleMutation.isPending || unarchiveCycleMutation.isPending
                }
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {archiveCycleMutation.isPending || unarchiveCycleMutation.isPending ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Please wait…
                  </>
                ) : cycleArchiveConfirm.isArchived ? (
                  'Restore'
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {processArchiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete</h3>
            <p className="mt-3 text-sm text-gray-600">
              {processArchiveConfirm.isArchived
                ? `Permanently delete archived process "${processArchiveConfirm.processName}"? This cannot be undone.`
                : `Delete process "${processArchiveConfirm.processName}"? This cannot be undone.`}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setProcessArchiveConfirm(null)}
                disabled={deleteProcessMutation.isPending}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const { cycleListKey, processListKey, processUuid } = processArchiveConfirm;
                  deleteProcessMutation.mutate(
                    { cycleListKey, processListKey, processUuid },
                    { onSettled: () => setProcessArchiveConfirm(null) }
                  );
                }}
                disabled={deleteProcessMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteProcessMutation.isPending ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Please wait…
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Cycle Risk Score Modal */}
      {editCycleRiskTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Adjust risk score
              </h3>
              <button
                type="button"
                onClick={handleCloseEditCycleRiskModal}
                disabled={patchCycleRiskScoreMutation.isPending}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mb-4 text-sm text-gray-600">
              {editCycleRiskTarget.cycleName}
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk score
              </label>
              <MaterialityRiskScoreSlider
                value={editCycleRiskScore}
                onChange={setEditCycleRiskScore}
                disabled={patchCycleRiskScoreMutation.isPending}
                showPresets={false}
                helperText={null}
                bandTilesSelectable
              />
            </div>

            {editCycleRiskError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{editCycleRiskError}</p>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={handleCloseEditCycleRiskModal}
                disabled={patchCycleRiskScoreMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEditCycleRisk}
                disabled={patchCycleRiskScoreMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {patchCycleRiskScoreMutation.isPending ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <span>Update</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Cycle Modal */}
      {showAddCycleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New SOX Cycle</h3>
              <button
                onClick={handleCloseCycleModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-4">
              {/* Cycle Selection Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Cycle Type
                </label>
                <select
                  value={cycleSelection}
                  onChange={(e) => setCycleSelection(e.target.value)}
                  disabled={isSavingCycle}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:bg-gray-50 disabled:opacity-70"
                >
                  <option value="">
                    {loadingSoxCycleDefaults ? 'Loading cycles from defaults…' : '-- Select a cycle --'}
                  </option>
                  {standardCyclesForModal.map((cycle) => (
                    <option key={cycle.id} value={cycle.id}>
                      {cycle.id} - {cycle.name}
                    </option>
                  ))}
                  <option value="custom">Custom cycle</option>
                </select>
                {loadingSoxCycleDefaults ? (
                  <p className="mt-1 text-xs text-gray-500">Standard options come from the defaults API.</p>
                ) : null}
                {soxDefaultsFetchError && cycleSelection !== 'custom' ? (
                  <p className="mt-1 text-xs text-red-600">
                    Could not load standard cycles
                    {soxDefaultsFetchErrorDetail?.message
                      ? `: ${soxDefaultsFetchErrorDetail.message}`
                      : '.'}{' '}
                    You can still create a custom cycle.
                  </p>
                ) : null}
                {!loadingSoxCycleDefaults &&
                !soxDefaultsFetchError &&
                standardCyclesForModal.length === 0 ? (
                  <p className="mt-1 text-xs text-amber-700">
                    No standard cycles are listed in your organization defaults. You can still create a
                    custom cycle.
                  </p>
                ) : null}
              </div>

              {/* Custom Cycle Fields (shown only when 'custom' is selected) */}
              {cycleSelection === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cycle ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customCycleId}
                      onChange={(e) => setCustomCycleId(e.target.value)}
                      placeholder="Cycle code (letters / numbers)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                    <p className="text-xs text-gray-500 mt-1">Saved as uppercase.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cycle Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customCycleName}
                      onChange={(e) => setCustomCycleName(e.target.value)}
                      placeholder="Display name for this cycle"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>

                </>
              )}

              {/* Risk score is required for all new cycles once a cycle type is selected. */}
              {cycleSelection && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Risk score
                  </label>
                  <MaterialityRiskScoreSlider
                    value={newCycleRiskScore}
                    onChange={setNewCycleRiskScore}
                    disabled={isSavingCycle}
                    showPresets={false}
                    helperText={null}
                    bandTilesSelectable
                  />
                </div>
              )}

              {/* Error Message */}
              {cycleError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{cycleError}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseCycleModal}
                disabled={isSavingCycle}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewCycle}
                disabled={isSavingCycle || !cycleSelection}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSavingCycle ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Add Cycle</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ProcessGenerateFlowchartModal
        isOpen={Boolean(generateFlowchartTarget)}
        onClose={() => setGenerateFlowchartTarget(null)}
        cycleId={generateFlowchartTarget?.cycleListKey}
        processId={generateFlowchartTarget?.processListKey}
        processName={generateFlowchartTarget?.process?.name}
        description={generateFlowchartTarget?.process?.description || ''}
        initialProcess={processDetailsForGenerate ?? generateFlowchartTarget?.process}
        isLoading={isLoadingProcessDetailsForGenerate}
        onGenerated={handleFlowchartGenerated}
      />
      </div>
    </>
  );
}

export default SOXDashboard;